import type { Business } from '../types/business';
import { canSellFromAnySite } from '../types/business';
import type { Employee } from '../types/entities';
import type { Site } from '../types/site';

type EmployeeSiteFields = Pick<Employee, 'defaultSiteId' | 'assignedSiteIds'>;

const activeSites = (sites: Site[]): Site[] => sites.filter((s) => s.isActive);

/** Parse Firestore `assignedSiteIds` (array ou absent). */
export function parseAssignedSiteIds(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const ids = Array.from(
    new Set(raw.filter((id): id is string => typeof id === 'string' && id.length > 0))
  );
  return ids.length > 0 ? ids : undefined;
}

/**
 * Ids d’affectation explicites (sans favori seul).
 * `defaultSiteId` seul ≠ restriction (comportement historique).
 */
export function resolveRawAssignedSiteIds(
  employee: EmployeeSiteFields | null | undefined
): string[] {
  if (!employee || !Array.isArray(employee.assignedSiteIds)) return [];
  return Array.from(
    new Set(
      employee.assignedSiteIds.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      )
    )
  );
}

/** Prefill admin : si pas d’affectation mais favori → suggérer `[defaultSiteId]`. */
export function suggestedAssignedSiteIdsFromLegacy(
  employee: EmployeeSiteFields | null | undefined
): string[] {
  const explicit = resolveRawAssignedSiteIds(employee);
  if (explicit.length > 0) return explicit;
  if (typeof employee?.defaultSiteId === 'string' && employee.defaultSiteId.length > 0) {
    return [employee.defaultSiteId];
  }
  return [];
}

/** Ids affectés encore présents parmi les lieux actifs. */
export function resolveAssignedSiteIds(
  employee: EmployeeSiteFields | null | undefined,
  sites: Site[]
): string[] {
  const activeIds = new Set(activeSites(sites).map((s) => s.id));
  return resolveRawAssignedSiteIds(employee).filter((id) => activeIds.has(id));
}

/**
 * Restriction vente / filtres : mode `any_site` + au moins un lieu dans `assignedSiteIds`.
 * Hors `any_site` ou sans affectation → pas de restriction (parc existant).
 */
export function isSalesSiteRestricted(
  employee: EmployeeSiteFields | null | undefined,
  business: Business | null,
  sites: Site[] = []
): boolean {
  if (!canSellFromAnySite(business)) return false;
  return resolveAssignedSiteIds(employee, sites).length > 0;
}

export function canAccessSite(
  employee: EmployeeSiteFields | null | undefined,
  siteId: string | undefined | null,
  business: Business | null,
  sites: Site[] = []
): boolean {
  if (!siteId) return false;
  if (!isSalesSiteRestricted(employee, business, sites)) return true;
  return resolveAssignedSiteIds(employee, sites).includes(siteId);
}

/** Lieux actifs accessibles (tous si non restreint). */
export function filterSitesForEmployee(
  sites: Site[],
  employee: EmployeeSiteFields | null | undefined,
  business: Business | null
): Site[] {
  const active = activeSites(sites);
  if (!isSalesSiteRestricted(employee, business, sites)) {
    return active;
  }
  const allowed = new Set(resolveAssignedSiteIds(employee, sites));
  return active.filter((s) => allowed.has(s.id));
}

/** Lieu principal parmi les lieux accessibles. */
export function resolvePrimarySalesSite(
  sites: Site[],
  employee: EmployeeSiteFields | null | undefined,
  business: Business | null
): Site | null {
  const accessible = filterSitesForEmployee(sites, employee, business);
  if (accessible.length === 0) return null;
  const preferredId = employee?.defaultSiteId;
  if (preferredId) {
    const preferred = accessible.find((s) => s.id === preferredId);
    if (preferred) return preferred;
  }
  return accessible[0] ?? null;
}

/** true si le siteId est dans la sélection (sélection vide = aucun résultat). */
export function siteIdInSelection(
  siteId: string | undefined | null,
  selectedSiteIds: string[]
): boolean {
  if (!siteId || selectedSiteIds.length === 0) return false;
  return selectedSiteIds.includes(siteId);
}

/** Init filtre multi-lieux : tous les lieux accessibles cochés. */
export function defaultSelectedSiteIds(accessibleSites: Site[]): string[] {
  return accessibleSites.map((s) => s.id);
}

export function isSiteFilterLocked(accessibleSites: Site[]): boolean {
  return accessibleSites.length === 1;
}

/** Garde vente : siteId doit être accessible si restriction. */
export function assertSalesSiteAllowed(
  employee: EmployeeSiteFields | null | undefined,
  siteId: string | undefined | null,
  business: Business | null,
  sites: Site[]
): void {
  if (!isSalesSiteRestricted(employee, business, sites)) return;
  if (!canAccessSite(employee, siteId, business, sites)) {
    throw new Error('Ce lieu de vente ne vous est pas autorisé.');
  }
}

/**
 * Normalise la payload employé : principal ∈ affectés ;
 * liste vide → pas de champ restriction.
 */
export function normalizeEmployeeSiteAssignment(input: {
  assignedSiteIds?: string[];
  defaultSiteId?: string;
}): { assignedSiteIds?: string[]; defaultSiteId?: string } {
  const assigned = Array.from(
    new Set(
      (input.assignedSiteIds ?? []).filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      )
    )
  );

  if (assigned.length === 0) {
    return {
      assignedSiteIds: undefined,
      defaultSiteId:
        typeof input.defaultSiteId === 'string' && input.defaultSiteId.length > 0
          ? input.defaultSiteId
          : undefined,
    };
  }

  let defaultSiteId =
    typeof input.defaultSiteId === 'string' && input.defaultSiteId.length > 0
      ? input.defaultSiteId
      : assigned[0];
  if (!assigned.includes(defaultSiteId)) {
    defaultSiteId = assigned[0];
  }

  return { assignedSiteIds: assigned, defaultSiteId };
}
